import { LAST_EXTERNAL_AUTH_ROUTE_KEY } from "@/constants/common";
import { setTimestampedRoute } from "@/utils/timestampedRoute";
import { useEffect } from "react";

// postMessage contract shared with irohana-rso-frontend
// (src/features/auth/components/ExternalAuthRouteBridge.tsx). Keep both sides in sync.
const SET_LAST_EXTERNAL_AUTH_ROUTE = "SET_LAST_EXTERNAL_AUTH_ROUTE";

type SetLastExternalAuthRouteMessage = {
  type: typeof SET_LAST_EXTERNAL_AUTH_ROUTE;
  value: string;
};

// Origins allowed to write the external auth route, sourced from env so it works
// across dev/stg/prod without code changes.
const ALLOWED_ORIGINS = [process.env.REACT_APP_FRONTEND_CLIENT_RSO_URL, process.env.REACT_APP_FRONTEND_ADMIN_RSO_URL]
  .filter((url): url is string => Boolean(url))
  .map((url) => new URL(url).origin);

const isSetLastExternalAuthRouteMessage = (data: unknown): data is SetLastExternalAuthRouteMessage => {
  if (typeof data !== "object" || data === null) return false;
  const message = data as SetLastExternalAuthRouteMessage;
  return message.type === SET_LAST_EXTERNAL_AUTH_ROUTE && typeof message.value === "string";
};

/**
 * Invisible bridge, loaded by external apps (RSO/visa portal) inside a hidden
 * iframe. It receives the external app's current authenticated route via
 * postMessage and persists it to center-origin localStorage, so the center login
 * flow can later return the user to that route. Renders nothing.
 */
const ExternalAuthBridge = () => {
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!ALLOWED_ORIGINS.includes(event.origin)) return;
      if (!isSetLastExternalAuthRouteMessage(event.data)) return;
      setTimestampedRoute(LAST_EXTERNAL_AUTH_ROUTE_KEY, event.data.value);
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  return null;
};

export default ExternalAuthBridge;
